import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'framer-motion';
import {
  LayoutDashboard,
  Megaphone,
  FolderOpen,
  BarChart3,
  TrendingUp,
  Settings,
  Search,
  Plus,
} from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CommandItem {
  id: string;
  group: string;
  icon: ReactNode;
  title: string;
  subtitle?: string;
  shortcut?: string;
  action: () => void;
}

interface CommandPaletteProps {
  extraItems?: CommandItem[];
}

function fuzzyMatch(query: string, text: string): boolean {
  const lower = text.toLowerCase();
  const q = query.toLowerCase();
  let qi = 0;
  for (let i = 0; i < lower.length && qi < q.length; i++) {
    if (lower[i] === q[qi]) {
      qi++;
    }
  }
  return qi === q.length;
}

const GROUP_ORDER = ['Recentes', 'Navegação', 'Ações Rápidas'];

export function CommandPalette({ extraItems = [] }: CommandPaletteProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const builtInItems = useMemo<CommandItem[]>(
    () => [
      {
        id: 'nav-dashboard',
        group: 'Navegação',
        icon: <LayoutDashboard size={16} />,
        title: 'Dashboard',
        subtitle: 'Visão geral do parceiro',
        shortcut: 'G D',
        action: () => navigate({ to: '/dashboard' }),
      },
      {
        id: 'nav-campaigns',
        group: 'Navegação',
        icon: <Megaphone size={16} />,
        title: 'Campanhas',
        subtitle: 'Gerenciar campanhas',
        shortcut: 'G C',
        action: () => navigate({ to: '/campaigns' }),
      },
      {
        id: 'nav-catalog',
        group: 'Navegação',
        icon: <FolderOpen size={16} />,
        title: 'Catálogo',
        subtitle: 'Produtos do parceiro',
        shortcut: 'G A',
        action: () => navigate({ to: '/catalog' }),
      },
      {
        id: 'nav-intelligence',
        group: 'Navegação',
        icon: <BarChart3 size={16} />,
        title: 'Inteligência',
        subtitle: 'Análise de mercado',
        shortcut: 'G I',
        action: () => navigate({ to: '/market-intelligence' }),
      },
      {
        id: 'nav-opportunities',
        group: 'Navegação',
        icon: <TrendingUp size={16} />,
        title: 'Oportunidades',
        subtitle: 'Oportunidades de negócio',
        shortcut: 'G O',
        action: () => navigate({ to: '/opportunities' }),
      },
      {
        id: 'nav-settings',
        group: 'Navegação',
        icon: <Settings size={16} />,
        title: 'Configurações',
        subtitle: 'Preferências do parceiro',
        shortcut: 'G S',
        action: () => navigate({ to: '/settings' }),
      },
      {
        id: 'action-new-campaign',
        group: 'Ações Rápidas',
        icon: <Plus size={16} />,
        title: 'Nova Campanha',
        subtitle: 'Criar nova campanha',
        shortcut: 'N',
        action: () => navigate({ to: '/campaigns' }),
      },
    ],
    [navigate],
  );

  const allItems = useMemo(() => [...builtInItems, ...extraItems], [builtInItems, extraItems]);

  const filtered = useMemo(() => {
    const q = query.trim();
    const items = q ? allItems.filter((item) => fuzzyMatch(q, item.title) || (item.subtitle && fuzzyMatch(q, item.subtitle))) : allItems;

    items.sort((a, b) => {
      const ai = GROUP_ORDER.indexOf(a.group);
      const bi = GROUP_ORDER.indexOf(b.group);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });

    return items;
  }, [allItems, query]);

  const groups = useMemo(() => {
    const map = new Map<string, CommandItem[]>();
    for (const item of filtered) {
      const list = map.get(item.group);
      if (list) {
        list.push(item);
      } else {
        map.set(item.group, [item]);
      }
    }
    return map;
  }, [filtered]);

  const flatItems = filtered;

  const executeItem = useCallback(
    (item: CommandItem) => {
      setOpen(false);
      setQuery('');
      item.action();
    },
    [],
  );

  // Global keyboard shortcut to open
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
        setQuery('');
        setSelectedIndex(0);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Reset selection when filtered results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Scroll selected item into view
  useEffect(() => {
    if (!listRef.current) return;
    const selected = listRef.current.querySelector('[data-selected="true"]');
    if (selected) {
      selected.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % flatItems.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev - 1 + flatItems.length) % flatItems.length);
          break;
        case 'Enter':
          e.preventDefault();
          if (flatItems[selectedIndex]) {
            executeItem(flatItems[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setOpen(false);
          setQuery('');
          break;
      }
    },
    [flatItems, selectedIndex, executeItem],
  );

  let itemIndex = -1;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            aria-hidden="true"
            onClick={() => {
              setOpen(false);
              setQuery('');
            }}
          />

          {/* Modal */}
          <motion.div
            className="relative w-full max-w-lg overflow-hidden rounded-xl border border-border bg-card shadow-2xl"
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.15 }}
            onKeyDown={handleKeyDown}
          >
            {/* Search input */}
            <div className="flex items-center gap-3 border-b border-border px-4">
              <Search size={16} className="shrink-0 text-muted-foreground" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar comandos..."
                className="h-12 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
              />
              <kbd className="hidden shrink-0 rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground sm:inline-block">
                ESC
              </kbd>
            </div>

            {/* Results */}
            <div ref={listRef} className="max-h-80 overflow-y-auto p-2">
              {/* Fica a mao: paleta de comandos reage a cada tecla, e o vazio
                  vive dentro do popover — mesma razao do autocomplete. */}
              {flatItems.length === 0 && (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                  Nenhum resultado encontrado.
                </div>
              )}

              {Array.from(groups.entries()).map(([group, items]) => (
                <div key={group}>
                  <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {group}
                  </div>
                  {items.map((item) => {
                    itemIndex++;
                    const idx = itemIndex;
                    const isSelected = idx === selectedIndex;
                    return (
                      <button
                        key={item.id}
                        data-selected={isSelected}
                        onClick={() => executeItem(item)}
                        onMouseEnter={() => setSelectedIndex(idx)}
                        className={cn(
                          'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors',
                          isSelected
                            ? 'bg-accent text-accent-foreground'
                            : 'text-foreground hover:bg-accent/50',
                        )}
                      >
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border bg-muted text-muted-foreground">
                          {item.icon}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium">{item.title}</div>
                          {item.subtitle && (
                            <div className="truncate text-xs text-muted-foreground">
                              {item.subtitle}
                            </div>
                          )}
                        </div>
                        {item.shortcut && (
                          <div className="flex shrink-0 items-center gap-1">
                            {item.shortcut.split(' ').map((key) => (
                              <kbd
                                key={key}
                                className="flex h-5 min-w-[20px] items-center justify-center rounded border border-border bg-muted px-1 text-[10px] font-medium text-muted-foreground"
                              >
                                {key}
                              </kbd>
                            ))}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="flex items-center gap-4 border-t border-border px-4 py-2 text-[11px] text-muted-foreground">
              <span className="flex items-center gap-1">
                <kbd className="rounded border border-border bg-muted px-1 py-0.5 text-[10px]">
                  &uarr;&darr;
                </kbd>{' '}
                navegar
              </span>
              <span className="flex items-center gap-1">
                <kbd className="rounded border border-border bg-muted px-1 py-0.5 text-[10px]">
                  &crarr;
                </kbd>{' '}
                selecionar
              </span>
              <span className="flex items-center gap-1">
                <kbd className="rounded border border-border bg-muted px-1 py-0.5 text-[10px]">
                  esc
                </kbd>{' '}
                fechar
              </span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
